/**
 * The company slide-over, and the V1 dossier inside it (WEB-10886, WEB-10892).
 *
 * Two screens show a company this way: the V2 workspace and the V1 Prospecting
 * Agent's Explore tab. The panel chrome and the restyle of the V1 dossier's
 * `dx-` classes therefore live here rather than in either screen's own sheet,
 * and both inject this one.
 *
 * The `--pw-*` tokens hang off `.pw-embed` rather than the workspace's
 * `.pw-root`, because on the V1 page there is no `.pw-root` above the drawer.
 * Inside the workspace the two declarations are identical, so the nested one
 * changes nothing.
 *
 * Every value is the design's (repos/designs/pav2a.html, frame 2a - b).
 */
const dossierDrawerStyles = `
.pw-embed {
  --pw-green-1: #e8f9f8;
  --pw-green-3: #41909b;
  --pw-success-2: #c8e6c9;
  --pw-info-2: #a3c6f5;
  --pw-alert-3: #fad668;
  --pw-danger-2: #fddccc;
  --pw-mono: 'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* The panel slides in from the edge it is pinned to. */
@keyframes pw-slide-in {
  from { opacity: 0; transform: translateX(24px); }
  to { opacity: 1; transform: translateX(0); }
}
.pw-slide-in { animation: pw-slide-in 220ms cubic-bezier(.2,.7,.2,1); }

/* A dossier still loading stands in as a shimmer rather than an empty panel. */
@keyframes pw-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

/* ── Hosting the V1 views ──────────────────────────────────────────────── */
.pw-embed.pa-app {
  display: block; height: auto; width: auto; background: transparent; overflow: visible;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  --pa-mono: var(--pw-mono);
}
.pw-embed .dx-back { display: none; }
.pw-embed .pa-loading, .pw-embed .dx-loading {
  min-height: 120px; padding: 0; margin: 14px 0;
  color: transparent; border-radius: 6px;
  background: linear-gradient(90deg, #edf1f5 25%, #f6f9fb 50%, #edf1f5 75%);
  background-size: 200% 100%;
  animation: pw-shimmer 1.4s ease-in-out infinite;
}

/* ── Company detail (the slide-over) ───────────────────────────────────── */
.pw-dossier .dx { max-width: none; }
.pw-dossier .dx-header {
  display: flex; flex-wrap: wrap; align-items: flex-start; gap: 14px;
  padding: 16px 58px 16px 18px; border-bottom: 1px solid #e9eef3;
}
.pw-dossier .dx-logo {
  width: 44px; height: 44px; border-radius: 8px; flex: none;
  background: #1d3d48; color: #fff;
  font-family: inherit; font-size: 18px; font-weight: 600; letter-spacing: 0;
}
.pw-dossier .dx-meta { flex: 1; min-width: 0; }
.pw-dossier .dx-name {
  font-family: inherit; font-size: 20px; font-weight: 600; color: #000;
  letter-spacing: 0; line-height: 1.2; margin: 0; gap: 10px;
}
.pw-dossier .dx-name a { font-family: var(--pw-mono); font-size: 11px; color: #9baabe; }
.pw-dossier .dx-name a svg { display: none; }
.pw-dossier .dx-score-chip {
  padding: 2px 10px; gap: 4px;
  background: #e8f5e9; border: 1px solid var(--pw-success-2); color: #1b5e20;
  font-family: var(--pw-mono); font-size: 13px; font-weight: 500;
}
.pw-dossier .dx-score-chip svg { display: none; }
.pw-dossier .dx-score--med { background: #fef9e7; border-color: var(--pw-alert-3); color: #664c00; }
.pw-dossier .dx-score--low { background: #edf1f5; border-color: #d3dae4; color: #4b5562; }
.pw-dossier .dx-pills { margin-top: 8px; gap: 6px; }
.pw-dossier .dx-pill {
  padding: 2px 9px; gap: 0;
  border: 1px solid #d3dae4; border-radius: 999px; background: transparent;
  color: #4b5562; font-size: 11px; font-weight: 400;
}
.pw-dossier .dx-pill svg { display: none; }
.pw-dossier .dx-actions {
  flex: 0 0 100%; flex-direction: row; flex-wrap: wrap; align-items: center;
  justify-content: flex-start; gap: 8px; margin-top: 0;
}
.pw-dossier .dx-btn {
  padding: 7px 13px; border-radius: 4px; gap: 6px;
  border: 1px solid #d3dae4; background: #fff; color: #1d3d48;
  font-size: 12px; font-weight: 400;
}
.pw-dossier .dx-btn:hover { background: #f6f9fb; }
.pw-dossier .dx-btn svg { display: none; }
.pw-dossier .dx-actions > div:first-child > .dx-btn { background: #1d3d48; border-color: #1d3d48; color: #fff; font-weight: 500; }
.pw-dossier .dx-actions > div:first-child > .dx-btn:hover { background: #182b35; }
.pw-dossier .dx-actions .dx-btn--primary { background: #c3e8e8; border-color: #c3e8e8; color: #1d3d48; font-weight: 500; }
.pw-dossier .dx-actions .dx-btn--primary:hover { background: #aedede; }

.pw-dossier .dx-crm-banner {
  margin: 14px 18px 0; padding: 9px 12px; gap: 10px;
  background: var(--pw-green-1); border-left: 3px solid var(--pw-green-3); border-radius: 0 6px 6px 0;
  font-size: 12px;
}
.pw-dossier .dx-crm-banner--new { background: #f6f9fb; border-left-color: #d3dae4; }
.pw-dossier .dx-crm-banner-icon { display: none; }
.pw-dossier .dx-crm-banner-text { color: #4b5562; }
.pw-dossier .dx-crm-banner-text strong { color: #1d3d48; font-weight: 500; }
.pw-dossier .dx-crm-banner--new .dx-crm-banner-text strong { color: #4b5562; }
.pw-dossier .dx-crm-banner-text em { font-style: normal; }
.pw-dossier .dx-crm-banner-text small { display: none; }
.pw-dossier .dx-crm-banner-link { color: #1d3d48; font-size: 12px; font-weight: 400; }

.pw-dossier .dx-loading { margin: 14px 18px; }
.pw-dossier .dx-section { margin: 14px 18px 0; }
.pw-dossier .dx-section--split { display: block; }
.pw-dossier .dx-section--split > div + div { margin-top: 14px; }
.pw-dossier .dx-section-h { gap: 8px; margin-bottom: 6px; }
.pw-dossier .dx-section-label {
  font-family: var(--pw-mono); font-size: 10px; font-weight: 400;
  letter-spacing: .08em; color: #6b7c93;
}
.pw-dossier .dx-section-count { font-family: var(--pw-mono); font-size: 10px; color: #9baabe; }
.pw-dossier .dx-prose { font-size: 13px; line-height: 1.6; color: #4b5562; text-wrap: pretty; }
.pw-dossier .dx-prose--empty { font-style: normal; font-size: 12px; color: #9baabe; }
.pw-dossier .dx-empty { font-size: 12px; color: #9baabe; }

.pw-dossier .dx-fin-strip {
  display: flex; gap: 0; background: #fff;
  border: 1px solid #e4eaf0; border-radius: 6px;
}
.pw-dossier .dx-fin {
  flex: 1; display: flex; flex-direction: column-reverse;
  padding: 9px 12px; border-right: 1px solid #e4eaf0;
}
.pw-dossier .dx-fin:last-child { border-right: none; }
.pw-dossier .dx-fin-v { font-family: var(--pw-mono); font-size: 14px; font-weight: 400; color: #2f3947; letter-spacing: 0; }
.pw-dossier .dx-fin-v--up { color: #1b5e20; }
.pw-dossier .dx-fin-l { font-family: var(--pw-mono); font-size: 10px; font-weight: 400; letter-spacing: 0; color: #6b7c93; margin: 0; }

.pw-dossier .dx-signals { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.pw-dossier .dx-signal {
  display: block; position: relative; padding: 9px 11px; padding-right: 40px;
  border-radius: 6px; border: 1px solid var(--pw-info-2); border-left-width: 1px; background: #e4edf7;
}
.pw-dossier .dx-signal--positive { border-color: var(--pw-success-2); background: #e8f5e9; }
.pw-dossier .dx-signal--warn { border-color: var(--pw-alert-3); background: #fef9e7; }
.pw-dossier .dx-signal--risk { border-color: var(--pw-danger-2); background: #f6e8e8; }
.pw-dossier .dx-signal-icon { display: none; }
.pw-dossier .dx-signal-head { display: block; margin-bottom: 3px; }
.pw-dossier .dx-signal-type {
  display: block; padding: 0; background: none; text-transform: none; letter-spacing: 0;
  font-size: 12px; font-weight: 600; color: #002c66;
}
.pw-dossier .dx-signal--positive .dx-signal-type { color: #1b5e20; }
.pw-dossier .dx-signal--warn .dx-signal-type { color: #664c00; }
.pw-dossier .dx-signal--risk .dx-signal-type { color: #7a1100; }
.pw-dossier .dx-signal-title { display: block; margin-top: 3px; font-size: 11px; font-weight: 400; line-height: 1.45; color: #4b5562; }
.pw-dossier .dx-signal-summary { display: none; }
.pw-dossier .dx-signal-foot { margin-top: 4px; font-size: 10px; color: #9baabe; }
.pw-dossier .dx-signal-foot a { color: #1d3d48; }
.pw-dossier .dx-conf { display: none; }
/* The separator belongs to the hidden confidence chip; without this it
   renders as a bullet floating in front of the source link. */
.pw-dossier .dx-signal-sep { display: none; }
.pw-dossier .dx-signal-date {
  position: absolute; top: 9px; right: 11px; margin: 0;
  font-family: var(--pw-mono); font-size: 10px; color: #002c66;
}
.pw-dossier .dx-signal--positive .dx-signal-date { color: #1b5e20; }
.pw-dossier .dx-signal--warn .dx-signal-date { color: #664c00; }
.pw-dossier .dx-signal--risk .dx-signal-date { color: #7a1100; }

.pw-dossier .dx-contacts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.pw-dossier .dx-contact {
  align-items: flex-start; gap: 9px; padding: 10px 11px;
  border: 1px solid #e4eaf0; border-radius: 6px; background: #fff;
}
.pw-dossier .dx-contact:hover { border-color: #e4eaf0; box-shadow: none; }
.pw-dossier .dx-contact--primary { border-color: #e4eaf0; background: #fff; }
.pw-dossier .dx-contact-av { width: 28px; height: 28px; font-size: 11px; font-weight: 600; }
.pw-dossier .dx-contact-name { font-size: 12px; font-weight: 400; color: #2f3947; gap: 6px; }
.pw-dossier .dx-contact-primary-tag {
  margin-left: auto; padding: 1px 5px; border-radius: 3px;
  font-size: 10px; font-weight: 400; letter-spacing: 0; text-transform: none;
  background: #c3e8e8; color: #1d3d48;
}
.pw-dossier .dx-contact-role { font-size: 11px; color: #6b7c93; margin-top: 0; }
.pw-dossier .dx-contact-source { display: none; }
/* Design: channel pills start at the card's left edge, not indented under the
   name column (avatar 28px + 9px gap). */
.pw-dossier .dx-contact-channels { margin-top: 8px; margin-left: -37px; gap: 4px; flex-wrap: wrap; }
/* The design rotates the contact avatars through green-3 / info-4 / rose-5;
   the inline avatarColors rotation is V1's, so the override needs the weight. */
.pw-dossier .dx-contact:nth-child(3n + 1) .dx-contact-av { background: #41909b !important; }
.pw-dossier .dx-contact:nth-child(3n + 2) .dx-contact-av { background: #1f5cad !important; }
.pw-dossier .dx-contact:nth-child(3n) .dx-contact-av { background: #86476b !important; }
.pw-dossier .dx-channel-pill {
  padding: 1px 5px; border-radius: 3px; gap: 3px;
  border: 1px solid #d3dae4; background: transparent; color: #6b7c93;
  font-size: 10px; font-weight: 400;
}
.pw-dossier .dx-channel-pill:hover { background: #f6f9fb; color: #1d3d48; }
.pw-dossier .dx-channel-pill svg { display: none; }
.pw-dossier .dx-channel-source {
  padding: 0; border-radius: 0; background: none;
  font-size: 10px; font-weight: 400; letter-spacing: 0; text-transform: none; color: #6b7c93;
}
.pw-dossier .dx-channel-source::before { content: '· '; }

.pw-dossier .dx-bullets { flex-direction: row; flex-wrap: wrap; gap: 6px; }
.pw-dossier .dx-bullet { padding: 3px 9px; gap: 0; border-radius: 4px; background: #edf1f5; }
.pw-dossier .dx-bullet-icon { display: none; }
.pw-dossier .dx-bullet-body { font-size: 11px; line-height: 1.4; color: #3e4957; }
.pw-dossier .dx-bullet-body strong { font-weight: 400; }
.pw-dossier .dx-chip { padding: 3px 9px; border-radius: 4px; background: #edf1f5; font-size: 11px; color: #3e4957; }

.pw-dossier .dx-meta-footer {
  margin: 14px 0 0; padding: 9px 18px; gap: 10px;
  border-top: 1px solid #e9eef3; background: #f6f9fb;
  font-size: 11px; color: #6b7c93;
}
.pw-dossier .dx-meta-footer svg { display: none; }
.pw-dossier .dx-meta-footer code { font-family: var(--pw-mono); font-size: 10px; color: #9baabe; background: none; padding: 0; }
`;

export default dossierDrawerStyles;
